/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Structural hard-fail validation (§15.10.1 hard bindings, §15.11 ingredient
 * manifest hash) — the `validation_state` bugs from the divergence audit.
 *
 *  - Hard-binding count on the binding (active) manifest: a standard manifest MUST
 *    carry exactly one hard binding (missing → `claim.hardBindings.missing`,
 *    >1 → `assertion.multipleHardBindings`); an update manifest MUST carry none
 *    (`manifest.update.invalid`). Scoped to the binding manifest to match c2pa-rs
 *    (`store.rs`: "verify the asset hash binding once for the whole store, on the
 *    binding manifest") and the WASM oracle — NOT every manifest, which would wrongly
 *    flag legitimately-zero-binding ingredient/update manifests.
 *  - Ingredient manifest hash: every ingredient assertion carrying a
 *    `c2pa_manifest`/`activeManifest` hashed-URI is checked against the referenced
 *    manifest's JUMBF box hash (§8.4.2.3). Mismatch (not explained by a redaction) →
 *    `ingredient.manifest.mismatch`; referenced manifest absent →
 *    `ingredient.manifest.missing`. Runs across all manifests (mirrors the recursive
 *    `ingredient_checks`).
 *  - Actions rules (§18.15.4.5): a `c2pa.created` action must record a
 *    `digitalSourceType` (→ `assertion.action.malformed`). Mirrors c2pa-rs
 *    `verify_actions`; v2 claims only. See {@link actionIssues}.
 */

import type { ParsedAssertion, ParsedManifest, ParsedStore } from '../model/parseManifestStore'
import { bytesEqual, hashJumbfBox, strictDigestName } from './hashJumbfBox'

export interface StructuralIssue {
    code: string
    manifestLabel: string
    /** URL suffix after `self#jumbf=/c2pa/<manifest>/`. Defaults to `c2pa.signature`. */
    suffix?: string
}

/** The version-stripped label root (c2pa-rs `label_root`: strips a trailing `.vN`). */
function labelRoot (label: string): string {
    const m = label.match(/^(.*)\.v\d+$/)
    return m ? m[1] : label
}

/**
 * A hard-binding-to-content assertion per C2PA 2.4 §15.10.1.2 (line 3518) and §11.2.2
 * (line 2022): label root is exactly one of `c2pa.hash.data`, `c2pa.hash.boxes`,
 * `c2pa.hash.collection.data`, `c2pa.hash.bmff` (covers `.v2`/`.v3`), excluding
 * multipart (`.part`) entries. `c2pa.hash.multi-asset` is deliberately NOT in the set
 * (it is not a hard binding to content; §15.10.1.3 forbids it on update manifests
 * separately).
 *
 * The UNVERSIONED `c2pa.hash.bmff` (v1) label is excluded: §18.6.1 (line 5246) says a
 * validator "shall ignore any c2pa.hash.bmff assertions, processing the manifest as if
 * the assertion were not present", and §15.10.1.2 lists only `.v2`/`.v3` as hard
 * bindings. So a manifest whose only binding is a v1 `c2pa.hash.bmff` has no hard
 * binding → `claim.hardBindings.missing` (fail-closed), rather than counting a binding
 * the Merkle/standalone path (keyed on `.v2`/`.v3`) never verifies. No fixture carries
 * an unversioned bmff binding.
 *
 * Spec-vs-c2pa-rs: the spec includes `c2pa.hash.collection.data`; c2pa-rs's
 * `hash_assertions()` omits it (a c2pa-rs gap — a collection-only manifest would be
 * wrongly flagged `claim.hardBindings.missing`). We follow the SPEC and include it
 * (no fixture uses it, so this diverges from the oracle only on collection-hash
 * manifests, which the spec explicitly counts).
 */
export function isHardBinding (label: string): boolean {
    if (label.endsWith('.part')) return false
    if (label === 'c2pa.hash.bmff') return false // v1 — §18.6.1: ignore (not a hard binding)
    const r = labelRoot(label)
    return r === 'c2pa.hash.data' || r === 'c2pa.hash.bmff' ||
        r === 'c2pa.hash.boxes' || r === 'c2pa.hash.collection.data'
}

/** The hard-binding-to-content label root (`c2pa.hash.bmff`/`data`/`boxes`/`collection.data`), or null. */
export function hardBindingRoot (label: string): string | null {
    return isHardBinding(label) ? labelRoot(label) : null
}

/**
 * The mismatch status code for a hard binding that this fragmented-BMFF reader does
 * NOT verify (`c2pa.hash.boxes`/`data`/`collection.data`) — so an unconfirmable active
 * hard binding is reported non-matching (§15.5.5) rather than silently Valid. Returns
 * null for `c2pa.hash.bmff` (handled by the Merkle / standalone path) and non-bindings.
 */
export function unverifiedHardBindingCode (label: string): string | null {
    switch (hardBindingRoot(label)) {
        case 'c2pa.hash.boxes': return 'assertion.boxesHash.mismatch'
        case 'c2pa.hash.data': return 'assertion.dataHash.mismatch'
        case 'c2pa.hash.collection.data': return 'assertion.collectionHash.mismatch'
        default: return null
    }
}

/**
 * Resolves the manifest whose hard binding governs the asset content (§15.12).
 * For a standard active manifest this is the active manifest itself. For an
 * *update* manifest (which carries no hard binding of its own) it is the first
 * standard manifest reached by walking the `parentOf` ingredient chain.
 *
 * Returns null when the walk finds no standard manifest (a broken/absent
 * `parentOf` chain) — the caller must then reject with `claim.hardBindings.missing`
 * (STATE-1). Mirrors the fork's `Store::get_hash_binding_manifest`, which emits
 * HARD_BINDINGS_MISSING on `None`.
 */
export function resolveBindingManifest (active: ParsedManifest, store: ParsedStore): ParsedManifest | null {
    let current: ParsedManifest | undefined = active
    const seen = new Set<string>()
    while (current && current.isUpdateManifest) {
        if (seen.has(current.label)) return null // parentOf cycle → unresolved
        seen.add(current.label)
        current = parentManifest(current, store)
    }
    return current ?? null
}

/** The standard/update manifest referenced by this manifest's `parentOf` ingredient, if any. */
function parentManifest (m: ParsedManifest, store: ParsedStore): ParsedManifest | undefined {
    for (const a of m.assertions) {
        if (!a.label.startsWith('c2pa.ingredient')) continue
        const d = (a.decoded && typeof a.decoded === 'object' ? a.decoded : null) as Record<string, unknown> | null
        if (!d || d.relationship !== 'parentOf') continue
        const ref = manifestRef(a.decoded)
        const urn = manifestUrn(ref?.url)
        if (urn) { const t = store.byLabel.get(urn); if (t) return t }
    }
    return undefined
}

/** Hard-binding count check for the binding (active) manifest. */
export function hardBindingCountIssues (binding: ParsedManifest): StructuralIssue[] {
    const count = binding.assertions.filter(a => isHardBinding(a.label)).length
    const issues: StructuralIssue[] = []
    if (!binding.isUpdateManifest) {
        if (count === 0) issues.push({ code: 'claim.hardBindings.missing', manifestLabel: binding.label, suffix: 'c2pa.claim' })
        else if (count > 1) issues.push({ code: 'assertion.multipleHardBindings', manifestLabel: binding.label })
    } else if (count > 0) {
        // §15.10.1.3: an update manifest must carry no hash assertions.
        issues.push({ code: 'manifest.update.invalid', manifestLabel: binding.label, suffix: 'c2pa.claim' })
    }
    return issues
}

/** The `{ url, hash, alg }` manifest reference an ingredient assertion carries, if any. */
function manifestRef (decoded: unknown): { url?: string, hash?: Uint8Array, alg?: string } | null {
    const d = (decoded && typeof decoded === 'object' ? decoded : null) as Record<string, unknown> | null
    if (!d) return null
    const ref = (d.c2pa_manifest ?? d.activeManifest) as Record<string, unknown> | undefined
    if (!ref || typeof ref !== 'object') return null
    return {
        url: typeof ref.url === 'string' ? ref.url : undefined,
        hash: ref.hash instanceof Uint8Array ? ref.hash : undefined,
        alg: typeof ref.alg === 'string' ? ref.alg : undefined
    }
}

function manifestUrn (url: string | undefined): string | null {
    if (!url) return null
    const m = url.match(/\/c2pa\/([^/]+)/)
    return m ? m[1] : null
}

function lastSegment (url: string): string {
    const i = url.lastIndexOf('/')
    return i >= 0 ? url.slice(i + 1) : url
}

/** True if every JUMBF content/padding box within an assertion superbox is all-0x00. */
function assertionContentZeroed (a: ParsedManifest['assertions'][number]): boolean {
    for (const child of a.box.children) {
        if (!child.data) continue
        for (const b of child.data) if (b !== 0) return false
    }
    return true
}

/**
 * §15.11.3.3.1 (STATE-3): "For each URI in the list of redacted assertions … if the
 * referenced assertion is present and any JUMBF Content box or padding box within it
 * contains anything other than … 0x00 bytes, the claim shall be rejected with
 * `assertion.notRedacted`." Listing a URI in `redacted_assertions` suppresses both the
 * hashed-URI recompute and the undeclared check, so without this an attacker could add
 * or modify an ingredient-manifest assertion, redact its URI, and ride the tampered
 * bytes through unvalidated. We verify each redacted assertion that is still present
 * was actually zeroed. Self-redaction (into the redacting manifest itself) is reported
 * separately as `assertion.selfRedacted` by validateAssertions, so is skipped here.
 */
export function redactionIssues (m: ParsedManifest, store: ParsedStore): StructuralIssue[] {
    const issues: StructuralIssue[] = []
    for (const uri of m.claim.redactedAssertions) {
        const urn = manifestUrn(uri)
        if (urn === null || urn === m.label) continue // self-redaction handled elsewhere
        const target = store.byLabel.get(urn)
        if (!target) continue
        const label = lastSegment(uri)
        const a = target.assertions.find(x => x.label === label)
        if (!a) continue // absent (redacted by removal) → nothing to verify
        if (!assertionContentZeroed(a)) {
            issues.push({ code: 'assertion.notRedacted', manifestLabel: m.label, suffix: `c2pa.assertions/${label}` })
        }
    }
    return issues
}

/** The claim-referenced actions assertions of a manifest (created ∪ gathered). */
function referencedActionsAssertions (m: ParsedManifest): ParsedAssertion[] {
    const referenced = new Set(
        [...m.claim.createdAssertions, ...m.claim.gatheredAssertions].map(r => r.label)
    )
    return m.assertions.filter(a => referenced.has(a.label) && labelRoot(a.label) === 'c2pa.actions')
}

/**
 * §18.15.4.5 (line 7112): "For all assets, a corresponding digitalSourceType field,
 * with an appropriate value, shall be recorded with the c2pa.created action" — line 7118
 * exempts `c2pa.opened`. c2pa-rs enforces it in `verify_actions` (claim.rs, comment
 * "2.b.v") as a gating `assertion.action.malformed` on the actions assertion's URI.
 *
 * Parity details, all deliberate:
 *  - **presence only.** c2pa-rs checks `action.source_type().is_none()`, so a present but
 *    non-conformant value (not an IPTC/C2PA term, or empty) passes. The spec's "appropriate
 *    value" requirement is therefore NOT enforced here either.
 *  - **v1 claims exempt.** `verify_actions` returns early for v1 unless
 *    `strict_v1_validation` is set, which is off by default; we have no such setting.
 *  - **every manifest, every claim-referenced actions assertion**, created and gathered
 *    (`verify_actions` runs from `verify_internal`, once per claim in the validation set).
 *  - **one issue per offending action**, matching c2pa-rs's per-action `log_item!`.
 *  - labels match via `labelRoot`, i.e. `c2pa.actions` and `c2pa.actions.v2` but not a
 *    `__N` duplicate instance — c2pa-rs's `label_root` strips a trailing `.vN` only, so
 *    it skips those too.
 *
 * This was the one known FALSE-ACCEPT divergence: without it, a manifest whose
 * `c2pa.created` action omits `digitalSourceType` reached `Trusted` here while the
 * reference reported `Invalid` (measured on the WDR/CCAV HLS streams).
 */
export function actionIssues (m: ParsedManifest): StructuralIssue[] {
    if (m.claimVersion === 1) return []
    const issues: StructuralIssue[] = []
    for (const a of referencedActionsAssertions(m)) {
        const data = (a.decoded && typeof a.decoded === 'object' ? a.decoded : {}) as Record<string, unknown>
        const actions = Array.isArray(data.actions) ? data.actions : []
        for (const entry of actions) {
            if (!entry || typeof entry !== 'object') continue
            const action = entry as Record<string, unknown>
            if (action.action === 'c2pa.created' && action.digitalSourceType === undefined) {
                issues.push({
                    code: 'assertion.action.malformed',
                    manifestLabel: m.label,
                    suffix: `c2pa.assertions/${a.label}`
                })
            }
        }
    }
    return issues
}

/**
 * Ingredient manifest-hash check for one manifest's ingredient assertions.
 * `redactedUrns` is the store-level set of redacted URIs — a mismatch attributable
 * to a redaction is tolerated (mirrors c2pa-rs `has_redactions`; the v2
 * claim-signature fallback is deferred).
 */
export async function ingredientManifestIssues (
    m: ParsedManifest, store: ParsedStore, redactedUris: string[]
): Promise<StructuralIssue[]> {
    const issues: StructuralIssue[] = []
    for (const a of m.assertions) {
        if (!a.label.startsWith('c2pa.ingredient')) continue
        const ref = manifestRef(a.decoded)
        // No manifest hashed-URI → the ingredient has no provenance (informational in
        // c2pa-rs, not a hard fail) — skip.
        if (!ref || !ref.hash || !ref.url) continue
        const urn = manifestUrn(ref.url)
        const suffix = `c2pa.assertions/${a.label}`
        const target = urn ? store.byLabel.get(urn) : undefined
        if (!target) {
            issues.push({ code: 'ingredient.manifest.missing', manifestLabel: m.label, suffix })
            continue
        }
        // Tolerate a mismatch explained by a redaction of the referenced manifest.
        const redacted = urn !== null && redactedUris.some(r => r.includes(urn))
        if (redacted) continue
        // Alg: the ref's alg, else the referenced claim's alg (c2pa-rs `ingredient.alg()`),
        // defaulting to SHA-256 as c2pa-rs does.
        const algName = strictDigestName(ref.alg ?? target.claim.alg) ?? 'SHA-256'
        const computed = await hashJumbfBox(store.buffer, target.box, algName)
        if (!bytesEqual(computed, ref.hash)) {
            issues.push({ code: 'ingredient.manifest.mismatch', manifestLabel: m.label, suffix })
        }
    }
    return issues
}
